import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"

import type { PlanViewMode } from "./plan-utils"

import { Maximize2, RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

/**
 * One plan section as a card (Document) or a fixed-size tile (Canvas). Canvas tiles
 * keep the row/col span from the section catalogue, clip overflow, and scroll INSIDE
 * the tile. AI tiles expose Update (+ Expand in Canvas); the manual §06 tile gets
 * Expand only.
 */
export function PlanTile({
  mode,
  no,
  title,
  updatable,
  canEdit,
  colSpan,
  rowSpan,
  onUpdate,
  onExpand,
  updateTestId,
  children,
}: {
  mode: PlanViewMode
  no: string
  title: string
  updatable: boolean
  canEdit: boolean
  colSpan: number
  rowSpan: number
  onUpdate?: () => void
  onExpand?: () => void
  updateTestId?: string
  children: ReactNode
}) {
  const { t } = useTranslation()
  const showUpdate = updatable && canEdit && Boolean(onUpdate)
  const showExpand = mode === "canvas" && Boolean(onExpand)

  const header = (
    <div className="flex items-start justify-between gap-2">
      <h3 className="text-muted-foreground text-xs font-semibold tracking-[0.12em] uppercase">
        <span className="text-muted-foreground/60 mr-1.5">{no}</span>
        {title}
      </h3>
      <div className="flex shrink-0 items-center gap-0.5">
        {showUpdate ? (
          <Button
            aria-label={t("segmentAnalysis.plan.update")}
            className="text-muted-foreground hover:text-primary size-8"
            data-testid={updateTestId}
            size="icon"
            title={t("segmentAnalysis.plan.update")}
            variant="ghost"
            onClick={onUpdate}
          >
            <RefreshCw className="size-3.5" />
          </Button>
        ) : null}
        {showExpand ? (
          <Button
            aria-label={t("segmentAnalysis.plan.expand")}
            className="text-muted-foreground size-8"
            size="icon"
            title={t("segmentAnalysis.plan.expand")}
            variant="ghost"
            onClick={onExpand}
          >
            <Maximize2 className="size-3.5" />
          </Button>
        ) : null}
      </div>
    </div>
  )

  if (mode === "canvas") {
    return (
      <article
        className="border-border bg-card flex flex-col overflow-hidden rounded-lg border p-4 shadow-sm"
        style={{ gridColumn: `span ${colSpan}`, gridRow: `span ${rowSpan}` }}
      >
        {header}
        <div className={cn("mt-3 min-h-0 flex-1 overflow-y-auto pr-1")}>{children}</div>
      </article>
    )
  }

  return (
    <article
      className="border-border bg-card scroll-mt-24 rounded-lg border p-4 shadow-sm"
      id={`sa-section-${no}`}
    >
      {header}
      <div className="mt-3">{children}</div>
    </article>
  )
}
